/**
 * `projectsApi` tags plus the org list: creating a project must refresh the org
 * tab it belongs to.
 */

import { apiSlice } from '@/store/apiSlice';
import { invalidateResource } from '@/features/permissions/can';
import { PROJECT_RESOURCE } from '@/features/permissions/model';
import type { DataEnvelope } from '@/features/orgs/types';
import type {
  CreateProjectRequest,
  CreateResourceRequest,
  CreateResourceResponse,
  DeleteResourceResponse,
  Project,
  ProjectResource,
  ProjectResourceType,
} from '../types';

/**
 * Design §14.5: when the server rejects a gated mutation with 403, the cached
 * verdict was wrong (a policy changed since the probe) — drop the project
 * verdicts so the next render re-evaluates and the control comes back (or stays
 * gone) on the server's answer, not ours.
 */
async function trackProjectMutation(
  _args: unknown,
  { queryFulfilled }: { queryFulfilled: PromiseLike<unknown> },
) {
  try {
    await queryFulfilled;
  } catch {
    invalidateResource(PROJECT_RESOURCE);
  }
}

function unwrap<T>(raw: DataEnvelope<T> | T | undefined): T | undefined {
  if (raw && typeof raw === 'object' && 'data' in (raw as DataEnvelope<T>)) {
    return (raw as DataEnvelope<T>).data;
  }
  return raw as T | undefined;
}

const projectsTaggedApi = apiSlice.enhanceEndpoints({
  addTagTypes: ['Project', 'ResourceList', 'OrgProjects', 'Organization'],
});

export const projectsApi = projectsTaggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `POST /projects` — `{name, description?, orgId}`. */
    createProject: builder.mutation<Project | undefined, CreateProjectRequest>({
      query: (body) => ({ url: 'projects', method: 'POST', body }),
      transformResponse: (raw: DataEnvelope<Project>) => unwrap<Project>(raw),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'OrgProjects', id: orgId },
        { type: 'Organization', id: orgId },
      ],
      onQueryStarted: trackProjectMutation,
    }),

    /** `GET /projects/:id` — the project header. */
    getProject: builder.query<Project | undefined, string>({
      query: (projectId) => ({ url: `projects/${projectId}` }),
      transformResponse: (raw: DataEnvelope<Project>) => unwrap<Project>(raw),
      providesTags: (_result, _error, projectId) => [{ type: 'Project', id: projectId }],
    }),

    /** `GET /projects/:id/resources?type=` — resource table (+ optional filter). */
    getResources: builder.query<
      ProjectResource[],
      { projectId: string; type?: ProjectResourceType | 'all' }
    >({
      query: ({ projectId, type }) => ({
        url: `projects/${projectId}/resources`,
        params: type && type !== 'all' ? { type } : undefined,
      }),
      transformResponse: (raw: DataEnvelope<ProjectResource[]> | ProjectResource[]) =>
        unwrap<ProjectResource[]>(raw) ?? [],
      providesTags: (_result, _error, { projectId }) => [{ type: 'ResourceList', id: projectId }],
    }),

    /** `POST /projects/:id/resources` — attach (`refId` is required by the service). */
    createResource: builder.mutation<
      CreateResourceResponse | undefined,
      CreateResourceRequest & { projectId: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `projects/${projectId}/resources`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: DataEnvelope<CreateResourceResponse>) =>
        unwrap<CreateResourceResponse>(raw),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ResourceList', id: projectId },
      ],
      onQueryStarted: trackProjectMutation,
    }),

    /** `DELETE /projects/:id/resources/:resourceId` — detach. */
    deleteResource: builder.mutation<
      DeleteResourceResponse | undefined,
      { projectId: string; resourceId: string }
    >({
      query: ({ projectId, resourceId }) => ({
        url: `projects/${projectId}/resources/${resourceId}`,
        method: 'DELETE',
      }),
      transformResponse: (raw: DataEnvelope<DeleteResourceResponse>) =>
        unwrap<DeleteResourceResponse>(raw),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ResourceList', id: projectId },
      ],
      onQueryStarted: trackProjectMutation,
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useGetProjectQuery,
  useGetResourcesQuery,
  useCreateResourceMutation,
  useDeleteResourceMutation,
} = projectsApi;
